// @flow
import * as React from 'react';
import { connect } from 'react-redux';
import * as Redux from 'redux';
import { translate } from "react-i18next";
import DefaultButton from '../../../../../components/DefaultButton';
import { start } from '../chunkUpload'
import {
    uploadSuccess,
    uploadFail,
    uploadProgress,
} from '../../../../../actions/upload';
import { getLayersConfig } from '../../../../../actions/map';
import { getPhotos } from '../../../../../actions/photos';
import { loadVideosDetails } from '../../../../../actions/video';
import { loadResourcesDetails } from '../../../../../actions/resource';
import { loadModelsDetails } from '../../../../../actions/model';
import { getFlights } from '../../../../../actions/flightManager';
import {
    PHOTOS_TYPE,
    VIDEO_TYPE,
    SHP_TYPE,
    ORTO_TYPE,
    DTM_TYPE,
    DRONE_TYPE,
    MODEL_TYPE,
    RESOURCE_TYPE,
} from '../../../../../types/upload';
import axios from 'axios';

const ORTO_TYPE_ID = 4;
const DTM_TYPE_ID = 5;
const SHP_TYP_ID = 6;

/*
    Changes in the "chosen mission" attribute (in upload panel) don't modify global chosenFlight,
    so in this case don't rely on chosenFlight from redux
*/
class UploadButton extends React.Component {
    async uploadPhotos(files, projectId) {
        const { axiosInstance, uploadProgress, uploadSuccess, uploadFail, getPhotos, getFlights, chosenFlight } = this.props;

        uploadProgress(0.001, files[0].name);
        try {
            for (let i = 0; i < files.length; i++) {
                const photoAttributes = {
                    projectId: projectId,
                    fileName: files[i].name,
                }

                const response = await axiosInstance.post('/photos', photoAttributes);
                const { data } = response;
                const { id } = data;

                const formData: FormData = new FormData();
                formData.append('file', files[i]);
                await axiosInstance.put(`/photos/${id}/upload`, formData);

                if (i + 1 < files.length) {
                    uploadProgress((i + 1) / files.length, files[i + 1].name);
                }
            }
            uploadSuccess(files.length);
            getFlights(axiosInstance);
            if (chosenFlight !== null && chosenFlight.id === projectId) {
                getPhotos(axiosInstance, projectId);
            }
        }
        catch (error) {
            uploadProgress(0);
            uploadFail(error.response ? error.response.data : null);
        }
    }

    uploadVideo(file, projectId) {
        const { axiosInstance, uploadProgress, uploadSuccess, uploadFail, loadVideosDetails, getFlights, chosenFlight } = this.props;

        uploadProgress(0.001, file.name);
        // prevent from conversion to UTC        
        const localDateTime = new Date(file.lastModified);
        localDateTime.setTime(localDateTime.getTime() - localDateTime.getTimezoneOffset() * 60 * 1000);

        const videoAttributes = {
            projectId: projectId,
            fileName: file.name,
            beginDateTime: localDateTime
        };

        axiosInstance.post('/videos', videoAttributes).then((response) => {
            const { data } = response;
            const { videoId, guid } = data;

            const url = `/videos/${videoId}/upload-chunk`;
            start(url, file, guid,
                (progress) => {
                    uploadProgress(progress);
                },
                () => {
                    uploadSuccess();
                    getFlights(axiosInstance);
                    if (chosenFlight !== null && chosenFlight.id === projectId) {
                        loadVideosDetails(axiosInstance, projectId, 'missions');
                    }
                },
                (error) => {
                    uploadProgress(0);
                    uploadFail(error);
                },
                axiosInstance);
        }).catch((error) => {
            uploadProgress(0);
            uploadFail(error.response ? error.response.data : null);   
        });
    }

    uploadModel(modelAttributes, onChangeAttributes) {
        const { axiosInstance, uploadProgress, uploadSuccess, uploadFail, loadModelsDetails, getFlights, chosenFlight } = this.props;
        const projectId = modelAttributes.projectId;
        const description = modelAttributes.description;
        const uid = modelAttributes.url ? modelAttributes.url.split('/')[4] : "0";
        uploadProgress(0.001, description);
        axios.get(`https://api.sketchfab.com/v3/models/${uid}`)
            .then(() => {
                axiosInstance.post('/models', modelAttributes)
                    .then(() => {
                        uploadProgress(0.001, description);
                        uploadSuccess();
                        getFlights(axiosInstance);
                        onChangeAttributes({
                            clear: true,
                            valid: false,
                            attributes: {
                                projectId: projectId,
                                url: "",
                                description: "",
                            }
                        });
                        if (chosenFlight !== null && chosenFlight.id === projectId) {
                            loadModelsDetails(axiosInstance, projectId);
                        }
                    })
                    .catch((error) => {
                        uploadProgress(0);
                        uploadFail(error.response ? error.response.data : null);
                    });
            })
            .catch((error) => {
                uploadProgress(0);
                uploadFail(error.response && error.response.status === 404 ? "Model not found" : null);       
            });
    }

    uploadLayer(file, layerAttributes, layerType) {
        const { axiosInstance, uploadProgress, uploadSuccess, uploadFail, getLayersConfig, getFlights, chosenFlight } = this.props;

        uploadProgress(0.001, file.name);
        axiosInstance.post('/layers/create-geoserver', layerAttributes).then((response) => {
            const { data } = response;
            const { layerId, guid } = data;

            const url = `/layers/${layerId}/upload-chunk`;
            start(url, file, guid,
                (progress) => {
                    uploadProgress(progress)
                },
                (isCanceled) => {
                    if (isCanceled) {
                        axiosInstance.delete(`/layers/delete-geoserver/${layerId}`)
                            .then(() => {
                                getFlights(axiosInstance);
                                if (chosenFlight !== null && chosenFlight.id === layerAttributes.projectId) {
                                    getLayersConfig(axiosInstance, layerAttributes.projectId);
                                }
                            })
                            .catch((error) => {
                                uploadProgress(0);
                                uploadFail(error);
                            })
                    } else {
                        uploadSuccess();
                        getFlights(axiosInstance);
                        if (chosenFlight !== null && chosenFlight.id === layerAttributes.projectId) {
                            getLayersConfig(axiosInstance, layerAttributes.projectId);
                        }
                    }
                },
                (error) => {
                    uploadProgress(0);
                    uploadFail(error);
                },
                axiosInstance);
        }).catch((error) => {
            uploadProgress(0);
            uploadFail(error.response ? error.response.data : null);          
        });
    }

    uploadResource(file, resourceAttributes) {
        const { axiosInstance, uploadProgress, uploadSuccess, uploadFail, loadResourcesDetails, getFlights, chosenFlight } = this.props;

        uploadProgress(0.001, file.name);
        axiosInstance.post('/resources', resourceAttributes).then((response) => {
            const { data } = response;
            const { resourceId, guid } = data;

            const url = `/resources/${resourceId}/upload-chunk`;
            start(url, file, guid,
                (progress) => {
                    uploadProgress(progress)
                },
                () => {
                    uploadSuccess();
                    getFlights(axiosInstance);
                    if (chosenFlight !== null && chosenFlight.id === resourceAttributes.projectId) {
                        loadResourcesDetails(axiosInstance, resourceAttributes.projectId);
                    }
                },
                (error) => {
                    uploadProgress(0);
                    uploadFail(error);
                },
                axiosInstance);
        }).catch((error) => {
            uploadProgress(0);
            uploadFail(error.response ? error.response.data : null);    
        });
    }

    uploadDrone(file, projectId, timeZoneName) {
        const { axiosInstance, uploadProgress, uploadSuccess, uploadFail, getFlights, chosenFlight } = this.props;

        uploadProgress(0.001, file.name);
        const formData: FormData = new FormData();
        formData.append('file', file);
        formData.append('timeZoneName', timeZoneName)

        axiosInstance.put(`/projects/${projectId}/upload-track`, formData).then((response) => {
            uploadSuccess();
            getFlights(axiosInstance);
            if (chosenFlight !== null && chosenFlight.id === projectId) {
                getPhotos(axiosInstance, projectId);
                loadVideosDetails(axiosInstance, projectId, 'missions');
            }
        }).catch((error) => {
            uploadProgress(0);
            uploadFail(error.response ? error.response.data : null);
        });
    }

    render() {
        const {
            disabled,
            loadType,
            files,
            groupAttributes,
            onChangeAttributes,
            t
        } = this.props;

        return (
            <DefaultButton
                style={{
                    bottom: '10px',
                    right: '10px',
                    position: 'absolute',
                    height: '25px',
                    width: '80px',
                }}
                disabled={disabled}
                onClick={() => {
                    if (!disabled) {
                        switch (loadType) {
                            case PHOTOS_TYPE:
                                this.uploadPhotos(files, groupAttributes.projectId);
                                break;
                            case VIDEO_TYPE:
                                this.uploadVideo(files[0], groupAttributes.projectId);
                                break;
                            case SHP_TYPE:
                                let layerAttributes = {
                                    projectId: groupAttributes.projectId,
                                    name: groupAttributes.layerName,
                                    layerType: SHP_TYP_ID
                                };
                                this.uploadLayer(files[0], layerAttributes);
                                break;
                            case ORTO_TYPE:
                                layerAttributes = {
                                    projectId: groupAttributes.projectId,
                                    name: groupAttributes.layerName,
                                    layerType: ORTO_TYPE_ID
                                };
                                this.uploadLayer(files[0], layerAttributes);
                                break;
                            case DTM_TYPE:
                                layerAttributes = {
                                    projectId: groupAttributes.projectId,
                                    name: groupAttributes.layerName,
                                    minValue: groupAttributes.minValue,
                                    maxValue: groupAttributes.maxValue,
                                    layerType: DTM_TYPE_ID
                                };
                                this.uploadLayer(files[0], layerAttributes);
                                break;
                            case DRONE_TYPE:
                                this.uploadDrone(files[0], groupAttributes.projectId, groupAttributes.timeZoneName);
                                break;
                            case MODEL_TYPE:
                                let modelAttributes = {
                                    projectId: groupAttributes.projectId, 
                                    url: groupAttributes.url, 
                                    description: groupAttributes.description
                                }
                                this.uploadModel(modelAttributes, onChangeAttributes);
                                break;
                            case RESOURCE_TYPE:
                                let resourceAttributes = {
                                    projectId: groupAttributes.projectId,
                                    name: groupAttributes.description,
                                    fileName: files[0].name,
                                }
                                this.uploadResource(files[0], resourceAttributes);
                                break;
                            default:
                                break;
                        }
                    }
                }}>
                {t('Upload')}
            </DefaultButton>
        );
    }
}

const mapStateToProps = state => ({
    axiosInstance: state.auth.get('axiosInstance'),
    chosenFlight: state.flightManager.get('chosenFlight')
});

const mapDispatchToProps = dispatch => Redux.bindActionCreators({
    uploadSuccess,
    uploadFail,
    uploadProgress,
    getLayersConfig,
    loadVideosDetails,
    getPhotos,
    loadResourcesDetails,
    loadModelsDetails,
    getFlights,
}, dispatch);

export default translate("translations")(connect(mapStateToProps, mapDispatchToProps)(UploadButton));


// WEBPACK FOOTER //
// ./src/containers/content/grid/uploadPanel/uploadButton/UploadButton.jsx